import React, { useMemo } from 'react';
import { StepContainer } from './steps/StepContainer';
import { FormSection } from './steps/form/formSection';
import { FormItem } from './steps/form/formItem';
import { estimationDataService } from '../../../services/estimationDataService';
import { CategoryData, UserSelection, MeasurementUnit } from '../../../types/estimation-items';
import { ITEM_DEPENDENCIES, SuggestionStrength } from '../../../data/dependencies';

interface DynamicCategoryStepProps {
  categoryId: string;
  data: CategoryData;
  onChange: (data: CategoryData) => void;
  onNext: () => void;
  onBack: () => void;
  crossStepSuggestions?: Record<string, { strength: SuggestionStrength; reason: string }>;
}

const getMeasurementPropFromUnit = (unit: MeasurementUnit): string => {
  switch (unit) {
    case 'm²':
      return 'withSurface';
    case 'ml':
    case 'm':
      return 'withLength';
    case 'unité':
      return 'withQuantity';
    case 'm³':
      return 'withVolume';
    case 'L':
      return 'withCapacity';
    case 'forfait':
      return 'none';
    case '%':
      return 'withPercentage';
    default:
      return 'none';
  }
};

export const DynamicCategoryStep: React.FC<DynamicCategoryStepProps> = ({
  categoryId,
  data = {},
  onChange,
  onNext,
  onBack,
  crossStepSuggestions = {},
}) => {
  const category = useMemo(() => {
    return estimationDataService.getCategoryData(categoryId);
  }, [categoryId]);

  const groupedItems = useMemo(() => {
    return estimationDataService.groupItemsBySubcategory(categoryId);
  }, [categoryId]);

  // Fusionne les suggestions intra-step (items cochés dans cette catégorie)
  // et cross-step (items cochés dans les autres catégories).
  // 'recommended' prime sur 'optional' en cas de conflit.
  const suggestions = useMemo(() => {
    const result: Record<string, { strength: SuggestionStrength; reason: string }> = {};

    // 1. Suggestions intra-step depuis les items cochés dans cette catégorie
    Object.entries(data).forEach(([key, sel]) => {
      if (sel?.selected) {
        ITEM_DEPENDENCIES[key]?.suggests.forEach(s => {
          if (
            !result[s.targetKey] ||
            (result[s.targetKey].strength === 'optional' && s.strength === 'recommended')
          ) {
            result[s.targetKey] = { strength: s.strength, reason: s.reason };
          }
        });
      }
    });

    // 2. Suggestions cross-step depuis les autres catégories (déjà calculées par DetailedEstimate)
    Object.entries(crossStepSuggestions).forEach(([targetKey, suggestion]) => {
      if (
        !result[targetKey] ||
        (result[targetKey].strength === 'optional' && suggestion.strength === 'recommended')
      ) {
        result[targetKey] = suggestion;
      }
    });

    return result;
  }, [data, crossStepSuggestions]);

  if (!category) {
    return (
      <StepContainer
        title="Catégorie introuvable"
        description="Cette catégorie n'existe pas"
        onNext={onNext}
        onBack={onBack}
        isValid={true}
      >
        <div className="text-red-600">
          Impossible de charger les données de cette catégorie.
        </div>
      </StepContainer>
    );
  }

  const updateItem = (itemKey: string, value: UserSelection) => {
    onChange({
      ...data,
      [itemKey]: value
    });
  };

  return (
    <StepContainer
      title={category.title}
      description={category.description}
      onNext={onNext}
      onBack={onBack}
      isValid={true}
    >
      <div className="space-y-6">
        {Object.entries(groupedItems).map(([subcategoryKey, items]) => {
          const subcategoryTitle = estimationDataService.getSubcategoryTitle(subcategoryKey, categoryId);
          const itemsArray = Object.entries(items);

          if (itemsArray.length === 0) return null;

          return (
            <FormSection
              key={subcategoryKey}
              title={subcategoryTitle}
              description=""
            >
              <div className="space-y-4">
                {itemsArray.map(([itemKey, item]) => {
                  const isSuggested = !data[itemKey]?.selected && !!suggestions[itemKey];

                  const props: any = {
                    label: item.name,
                    value: data[itemKey] || { selected: false },
                    onChange: (value: UserSelection) => updateItem(itemKey, value),
                    itemDetails: item.details,
                    itemKey: itemKey,
                    ...(isSuggested ? { suggestion: suggestions[itemKey] } : {}),
                  };

                  if (item.customPricing) {
                    props.withCustomAmount = true;
                  } else {
                    const measurementProp = getMeasurementPropFromUnit(item.unit);
                    if (measurementProp !== 'none') {
                      props[measurementProp] = true;
                    }
                  }

                  return (
                    <div key={itemKey}>
                      <FormItem {...props} />
                      {item.description && (
                        <div className="ml-8 mt-1">
                          <p style={{ fontSize: 11, color: '#7C6FA0', fontStyle: 'italic' }}>
                            {item.description}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </FormSection>
          );
        })}
      </div>
    </StepContainer>
  );
};

export default DynamicCategoryStep;
